/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Canonical English labels for shipped locales. Intl.DisplayNames would otherwise
 * emit "Canadian French" / "Mexican Spanish" / "Brazilian Portuguese"; product copy
 * uses the "Language (Region)" form.
 */
const ENGLISH_LABELS: Record<string, string> = {
  en: 'English',
  'zh-cn': 'Chinese (China)',
  nl: 'Dutch',
  'fr-ca': 'French (Canada)',
  de: 'German',
  it: 'Italian',
  ko: 'Korean',
  'pt-br': 'Portuguese (Brazil)',
  ru: 'Russian',
  es: 'Spanish',
  'es-mx': 'Spanish (Mexico)',
  th: 'Thai',
  vi: 'Vietnamese',
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** English list label for a language code; falls back to Intl, then the raw code. */
export function languageListLabel(code: string): string {
  const normalized = code.trim().toLowerCase();
  const mapped = ENGLISH_LABELS[normalized];
  if (mapped) return mapped;
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
    return name ? cap(name) : code;
  } catch {
    return code;
  }
}

export type ListedLanguage = { code: string; label: string };

/**
 * Selected language first, then the rest A-Z by English label so the default
 * (English) list reads: English, Chinese (China), Dutch, ...
 */
export function sortLanguageList<T extends ListedLanguage>(
  languages: T[],
  selectedCode: string,
): T[] {
  const selected = selectedCode.trim().toLowerCase();
  const collator = new Intl.Collator('en', { sensitivity: 'base' });
  return [...languages].sort((a, b) => {
    const aSelected = a.code.toLowerCase() === selected;
    const bSelected = b.code.toLowerCase() === selected;
    if (aSelected !== bSelected) return aSelected ? -1 : 1;
    return collator.compare(a.label, b.label) || a.code.localeCompare(b.code);
  });
}
